import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icon, Input, Text } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../utils/responsive'
import { useTranslation } from 'next-i18next'
import { ACCEPT_FILE_MIME, MAX_FILE_LENGTH, MAX_FILE_SIZE, MAX_NOTE_LENGTH } from '../../../model'
import { useDisputeInfoModalContext } from '../DisputeInfoContext'
import { useProcessDispute } from '../hooks'
import { TrackOnMount, useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'

const I18N_NAMESPACE = ['dispute']

export const SubmitEvidence: FC = () => {
  const { textSize, smallTextSize } = useConsumerSizes()
  const { t } = useTranslation(I18N_NAMESPACE)
  const { disputeInfo, isSubmitEvidence } = useDisputeInfoModalContext()
  const { reject, submitEvidence, isCommentError, isSubmitEvidenceError } = useProcessDispute()
  const uploadRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const addFileButtonText = useMemo(() => {
    if (files.length > 0) {
      return t('dispute:submitEvidence:addAnotherFile')
    }
    return t('dispute:submitEvidence:addFile')
  }, [t, files])
  const accept = useMemo<string>(() => {
    return ACCEPT_FILE_MIME.join(',')
  }, [])
  const [title, notePlaceholder, tips] = useMemo(() => {
    return [
      isSubmitEvidence ? t('dispute:info:submitEvidence:submitTitle') : t('dispute:info:submitEvidence:subtitle'),
      isSubmitEvidence ? t('dispute:submitEvidence:submitNotePlaceholder') : t('dispute:submitEvidence:notePlaceholder'),
      t('dispute:info:submitEvidence:tips'),
    ]
  }, [t, isSubmitEvidence])

  const noteChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setNote(event.target.value)
      setError('')
    },
    [setNote]
  )
  const addFileButtonClickHandler = useCallback(() => {
    uploadRef.current?.click()
  }, [])
  const fileChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files ?? [])
      if (fileList.length + files.length > MAX_FILE_LENGTH) {
        setError(t('dispute:submitEvidence:error:exceedCount'))
        return
      }

      const fileOutOfSizeList = fileList.filter((file) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError(`${t('dispute:submitEvidence:error:exceedSize')}`)
        return
      }

      setFiles([...files, ...fileList])
      setError('')
      event.target.value = ''
    },
    [files, setFiles, t]
  )
  const removeFileHandler = useCallback(
    (index: number) => () => {
      const dummyFiles = [...files]
      dummyFiles.splice(index, 1)
      setFiles([...dummyFiles])
      setError('')
    },
    [files, setFiles]
  )
  const submitHandler = useCallback(() => {
    if (note.trim() === '') {
      setError(t('dispute:info:submitEvidence:error:descriptionIsBlank'))
      return
    }
    const formData = new FormData()
    formData.append('content', note.trim())

    files.forEach((file) => {
      formData.append('files', file, file.name)
    })

    if (isSubmitEvidence) {
      submitEvidence({
        id: disputeInfo?.disputeEntities[0]?.id,
        formData,
      })
    } else {
      reject({
        id: disputeInfo?.disputeEntities[0]?.id,
        formData,
      })
      logEvent(TrackingEvent.PRESSED_DISPUTE_INFO_REJECT_SUBMIT)
    }
  }, [disputeInfo?.disputeEntities, files, note, reject, t, logEvent, isSubmitEvidence])

  useEffect(() => {
    return () => {
      setNote('')
      setFiles([])
    }
  }, [])

  useEffect(() => {
    if (isCommentError || isSubmitEvidenceError) {
      setError(t('dispute:info:processMessage:error'))
    }
  }, [isCommentError, isSubmitEvidenceError])

  return (
    <>
      <TrackOnMount eventName={TrackingEvent.VIEWED_DISPUTE_INFO_REJECT} />
      <Text size={textSize} bold>
        {title}
      </Text>
      <div className='mt-2.5'>
        <Input.TextArea rows={6} fullWidth={true} placeholder={notePlaceholder} onChange={noteChangedHandler} characterLimit={MAX_NOTE_LENGTH} />
      </div>
      <div className='my-2.5'>
        <Text bold size={textSize}>
          {tips}
        </Text>
      </div>
      <div className='mt-0'>
        {files.map((file, index) => {
          return (
            <div className='mt-2 flex items-center justify-between' key={`${file.name}${index}`}>
              <div className='mr-1 flex-auto overflow-hidden text-ellipsis whitespace-nowrap'>
                <Text size={textSize} bold renderAs='span'>
                  {file.name}
                </Text>
              </div>
              <div className='shrink-0 grow-0 cursor-pointer' onClick={removeFileHandler(index)}>
                <Icon.Close size='Small' />
              </div>
            </div>
          )
        })}
        <div className='mt-2'>
          <Button.Ghost size='Small' disabled={files.length >= MAX_FILE_LENGTH} onClick={addFileButtonClickHandler}>
            {addFileButtonText}
          </Button.Ghost>
        </div>
        <input ref={uploadRef} className='hidden' type='file' multiple={true} accept={accept} onChange={fileChangedHandler} />
      </div>
      <div>
        <Text size={smallTextSize} color='Gray40'>
          {t('dispute:submitEvidence:disclaim')}
        </Text>
      </div>
      {error && (
        <div className='mt-2'>
          <Text size={textSize} color='Fire'>
            {error}
          </Text>
        </div>
      )}
      <div className='mt-4'>
        <Button padding='Fluid' onClick={submitHandler}>
          {t('dispute:submitEvidence:submit')}
        </Button>
      </div>
    </>
  )
}
